/**
 * Shared helpers for matching inventory location barcodes / typed text
 * (hardware wedge, camera scan, manual entry) across mobile devices.
 */

export function safeLocationPart(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function parseScannedValue(raw) {
  if (typeof raw === "string") return raw;
  if (raw && typeof raw.data === "string") return raw.data;
  if (raw?.nativeEvent && typeof raw.nativeEvent.data === "string") return raw.nativeEvent.data;
  if (raw == null) return "";
  return String(raw);
}

/** Underlying DOM node for an RN Web TextInput ref (input or textarea). */
export function getTextInputDomNode(ref) {
  const el = ref && ref.current;
  if (!el) return null;
  const tag = (el.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA") return el;
  if (typeof el.querySelector === "function") {
    return el.querySelector("input, textarea");
  }
  return null;
}

/** Strip control chars (Zebra/DataWedge often suffix \\r\\n\\t). */
export function cleanScannedText(raw) {
  return parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
}

/**
 * Clean Android/Sincoole wedge noise on commit only — strips prefix garbage but
 * keeps the full hyphenated barcode (never truncates at the first segment).
 */
export function normalizeWedgeScanText(raw) {
  let text = parseScannedValue(raw);
  text = text.replace(/[\x00-\x1F\x7F]/g, "");
  text = text.replace(/^\\+(?:0)+/, "");
  text = text.trim();
  // Strip leading slashes/backslashes before stripping leading digits,
  // so "/000026site1/bin1" → "000026site1/bin1" → "site1/bin1".
  text = text.replace(/^[/\\]+/, "");
  text = text.replace(/^[\d\\]+(?=[A-Za-z])/, "");
  text = text.trim();
  const hyphenated = text.match(/[A-Za-z0-9]+(?:-[A-Za-z0-9]+)+/);
  if (hyphenated) return hyphenated[0];
  if (text.includes("-")) {
    return text.replace(/-+$/, "");
  }
  // Allow "/" as a valid separator inside a barcode (e.g. "site1/bin1").
  // The previous [A-Za-z0-9]+ regex truncated at the first slash.
  const plain = text.match(/[A-Za-z0-9][A-Za-z0-9/]*/);
  if (plain) return plain[0];
  return text.trim();
}

/** Strip control chars (Zebra/DataWedge often suffix \\r\\n) and normalize separators for comparison. */
export function normalizeScannedLocation(data) {
  var scanned = parseScannedValue(data)
    .replace(/[\x00-\x1F\x7F]/g, "")
    .trim();
  if (!scanned) return "";

  var scannedArray = scanned.split("-");
  if (scannedArray.length <= 1) {
    scannedArray = scanned.split("/");
  }
  if (scannedArray.length > 1) {
    scanned = "";
    scannedArray.forEach((part) => {
      scanned += part.trim().replace(/ /g, "");
    });
  } else {
    scanned = scanned.replace(/ /g, "");
  }
  return scanned.toLowerCase();
}

export function renderLocationLabel(location) {
  if (location == null || location === "") return "";
  if (typeof location === "string") return location;
  var item = location;
  if (typeof item === "object" && item?.length > 0) {
    item = item[0];
    if (typeof item === "string") return item;
  }
  if (!item || typeof item !== "object") return "";

  var completeLocation = "";
  if (item?.site?.locationName) completeLocation += safeLocationPart(item.site.locationName);
  if (item?.zone?.locationName) completeLocation += "-" + safeLocationPart(item.zone.locationName);
  if (item?.aisle?.locationName) completeLocation += "-" + safeLocationPart(item.aisle.locationName);
  if (item?.rackBay?.locationName) completeLocation += "-" + safeLocationPart(item.rackBay.locationName);
  if (item?.shelfLevel?.locationName) completeLocation += "-" + safeLocationPart(item.shelfLevel.locationName);
  if (item?.bin?.locationName) completeLocation += "-" + safeLocationPart(item.bin.locationName);
  return completeLocation;
}

/** Concatenated path without separators (legacy scan format). */
export function getLocationMatchKey(location) {
  var completeLocation = "";
  if (location?.site?.locationName) completeLocation += safeLocationPart(location.site.locationName);
  if (location?.zone?.locationName) completeLocation += safeLocationPart(location.zone.locationName);
  if (location?.aisle?.locationName) completeLocation += safeLocationPart(location.aisle.locationName);
  if (location?.rackBay?.locationName) completeLocation += safeLocationPart(location.rackBay.locationName);
  if (location?.shelfLevel?.locationName) completeLocation += safeLocationPart(location.shelfLevel.locationName);
  if (location?.bin?.locationName) completeLocation += safeLocationPart(location.bin.locationName);
  return completeLocation.trim().replace(/ /g, "").replace(/[/-]+/g, "").toLowerCase();
}

function uniqueByLocationId(candidates) {
  var seen = {};
  return candidates.filter((loc) => {
    var key = getLocationMatchKey(loc);
    if (!key || seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

/**
 * Resolve a scan or typed string to one inventory location object, or null if ambiguous / not found.
 */
export function findLocationInList(scannedRaw, locations, { exactOnly = false } = {}) {
  if (!Array.isArray(locations) || !locations.length) return null;

  var norm = normalizeScannedLocation(scannedRaw);
  if (!norm) return null;

  var exactKey = locations.filter((loc) => getLocationMatchKey(loc) === norm);
  exactKey = uniqueByLocationId(exactKey);
  if (exactKey.length === 1) return exactKey[0];

  var exactLabel = locations.filter(
    (loc) => normalizeScannedLocation(renderLocationLabel(loc)) === norm
  );
  exactLabel = uniqueByLocationId(exactLabel);
  if (exactLabel.length === 1) return exactLabel[0];

  //if (exactOnly) return null;

  var suffixMatches = locations.filter((loc) => {
    var key = getLocationMatchKey(loc);
    return key && (key.endsWith(norm) || norm.endsWith(key));
  });
  suffixMatches = uniqueByLocationId(suffixMatches);
  if (suffixMatches.length === 1) return suffixMatches[0];

  var includesMatches = locations.filter((loc) => {
    var key = getLocationMatchKey(loc);
    var labelNorm = normalizeScannedLocation(renderLocationLabel(loc));
    return (
      (key && (key.includes(norm) || norm.includes(key))) ||
      (labelNorm && (labelNorm.includes(norm) || norm.includes(labelNorm)))
    );
  });
  includesMatches = uniqueByLocationId(includesMatches);
  if (includesMatches.length === 1) return includesMatches[0];

  return null;
}

export function locationListIncludesTerm(locations, term) {
  var norm = normalizeScannedLocation(term);
  if (!norm) return false;
  return locations.some((loc) => {
    var key = getLocationMatchKey(loc);
    var labelNorm = normalizeScannedLocation(renderLocationLabel(loc));
    return (
      key === norm ||
      labelNorm === norm ||
      key.includes(norm) ||
      labelNorm.includes(norm)
    );
  });
}

/* ---- Android Chrome + Symbol wedge: buffer keystrokes, block Enter ---- */

const CAPTURE_OPTS = { capture: true, passive: false };
const POST_SCAN_ENTER_MS = 2500;
const IDLE_FLUSH_MS = 500;
const MIN_SCAN_LENGTH = 2;

let wedgeSessionActive = false;
let scanCallback = null;
let progressCallback = null;
let keyBuffer = "";
let idleFlushTimer = null;
let enterShieldActive = false;
let blockEnterUntil = 0;
let wedgeTeardown = null;
let lastKeyAt = 0;
let captureInput = null;
let activeWedgeOwnerId = null;
let fallbackScanGetter = null;
let globalEnterTrapCount = 0;
let globalEnterTrapTeardown = null;

function isWedgeRelatedTarget(target) {
  if (!target || typeof target.getAttribute !== "function") return false;
  if (target.getAttribute("data-wedge-scan") === "true") return true;
  if (target.getAttribute("data-wedge-capture") === "true") return true;
  if (typeof target.closest === "function") {
    return !!target.closest("[data-wedge-scan],[data-wedge-capture]");
  }
  return false;
}

function shouldTrapChromeEnter(e) {
  if (wedgeSessionActive || enterShieldActive || Date.now() < blockEnterUntil) {
    return true;
  }
  if (isEnterKey(e) || isTabKey(e)) {
    const target = e?.target;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (isWedgeRelatedTarget(target) || isWedgeRelatedTarget(active)) {
      return true;
    }
  }
  return false;
}

function onGlobalEnterTrap(e) {
  if (!globalEnterTrapCount || !shouldTrapChromeEnter(e)) return;
  if (!isEnterKey(e) && !isTabKey(e)) return;
  swallowEnter(e);
  if (wedgeSessionActive) {
    completeBufferedScan();
  }
}

function attachGlobalEnterTrap() {
  if (globalEnterTrapTeardown || typeof window === "undefined") return;
  window.addEventListener("keydown", onGlobalEnterTrap, CAPTURE_OPTS);
  window.addEventListener("keypress", onGlobalEnterTrap, CAPTURE_OPTS);
  window.addEventListener("keyup", onGlobalEnterTrap, CAPTURE_OPTS);
  globalEnterTrapTeardown = () => {
    window.removeEventListener("keydown", onGlobalEnterTrap, CAPTURE_OPTS);
    window.removeEventListener("keypress", onGlobalEnterTrap, CAPTURE_OPTS);
    window.removeEventListener("keyup", onGlobalEnterTrap, CAPTURE_OPTS);
  };
}

/** Ref-counted Enter trap for Shipment-by-Location on Android Chrome (survives child remounts). */
export function installGlobalChromeEnterTrap() {
  globalEnterTrapCount += 1;
  attachGlobalEnterTrap();
}

export function releaseGlobalChromeEnterTrap() {
  globalEnterTrapCount = Math.max(0, globalEnterTrapCount - 1);
  if (globalEnterTrapCount === 0 && globalEnterTrapTeardown) {
    globalEnterTrapTeardown();
    globalEnterTrapTeardown = null;
  }
}

export function getActiveWedgeOwnerId() {
  return activeWedgeOwnerId;
}

function containsTerminator(text) {
  return /[\r\n\t]/.test(text || "");
}

function ensureCaptureInput() {
  if (typeof document === "undefined") return null;
  if (captureInput) return captureInput;

  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "none";
  input.enterKeyHint = "done";
  input.setAttribute("data-wedge-capture", "true");
  input.setAttribute("aria-hidden", "true");
  input.setAttribute("autocomplete", "off");
  input.setAttribute("autocorrect", "off");
  input.setAttribute("autocapitalize", "off");
  input.setAttribute("spellcheck", "false");
  Object.assign(input.style, {
    position: "fixed",
    bottom: "0",
    left: "0",
    width: "100%",
    height: "44px",
    opacity: "0.01",
    padding: "0",
    margin: "0",
    border: "none",
    outline: "none",
    pointerEvents: "auto",
    zIndex: "2147483646",
    fontSize: "16px",
    background: "transparent",
    color: "transparent",
    caretColor: "transparent",
  });

  input.addEventListener("input", () => {
    if (!wedgeSessionActive) return;
    const val = input.value || "";
    if (containsTerminator(val)) {
      keyBuffer = val;
      completeBufferedScan();
      return;
    }
    if (val.length >= keyBuffer.length) {
      keyBuffer = val;
      if (progressCallback && keyBuffer.length >= 1 && !fallbackScanGetter) {
        try {
          progressCallback(cleanScannedText(keyBuffer));
        } catch (e) {
          console.warn("symbolWedgeSession progress callback:", e);
        }
      }
      scheduleIdleFlush();
    }
  });

  document.body.appendChild(input);
  captureInput = input;
  return input;
}

export function isSymbolWedgeSessionActive() {
  return wedgeSessionActive;
}

export function focusSymbolWedgeCapture() {
  if (!wedgeSessionActive) return;
  const node = ensureCaptureInput();
  if (!node) return;
  try {
    node.focus({ preventScroll: true });
  } catch (e) {
    node.focus();
  }
}

export function isSymbolWedgeCaptureFocused() {
  return typeof document !== "undefined" && document.activeElement === captureInput;
}

function clearCaptureInputValue() {
  if (captureInput) captureInput.value = "";
}

function isEnterKey(e) {
  const key = e?.key;
  const code = e?.keyCode || e?.which;
  return key === "Enter" || key === "Return" || code === 13;
}

function isTabKey(e) {
  return e?.key === "Tab" || e?.keyCode === 9;
}

function swallowEnter(e) {
  e.preventDefault();
  if (typeof e.stopImmediatePropagation === "function") {
    e.stopImmediatePropagation();
  }
  if (typeof e.stopPropagation === "function") {
    e.stopPropagation();
  }
}

function shouldBlockEnterNow() {
  return wedgeSessionActive || enterShieldActive || Date.now() < blockEnterUntil;
}

function keyEventToChar(e) {
  const key = e.key;
  if (key && key.length === 1 && key !== "Unidentified") return key;
  const code = e.keyCode || e.which;
  if (!code) return "";
  if (code >= 48 && code <= 57) return String.fromCharCode(code);
  if (code >= 65 && code <= 90) return String.fromCharCode(code);
  if (code >= 97 && code <= 122) return String.fromCharCode(code);
  if (code >= 32 && code <= 126) return String.fromCharCode(code);
  return "";
}

function clearIdleFlush() {
  if (idleFlushTimer) {
    clearTimeout(idleFlushTimer);
    idleFlushTimer = null;
  }
}

function scheduleIdleFlush() {
  clearIdleFlush();
  idleFlushTimer = setTimeout(() => {
    idleFlushTimer = null;
    completeBufferedScan();
  }, IDLE_FLUSH_MS);
}

function isGarbledKeyBuffer(text) {
  const clean = cleanScannedText(text);
  return clean.length >= 2 && /^(.)\1*$/.test(clean);
}

/** Android Chrome wedge keydown buffers are often repeated garbage (uuuuu, 0000). */
export function isGarbledWedgeScanText(text) {
  return isGarbledKeyBuffer(text);
}

function readPendingScan() {
  let fromFallback = "";
  if (typeof fallbackScanGetter === "function") {
    try {
      fromFallback = cleanScannedText(fallbackScanGetter());
    } catch (e) {}
  }
  const fromCapture = cleanScannedText(captureInput?.value || "");

  if (typeof fallbackScanGetter === "function") {
    const domValue = fromFallback.length >= fromCapture.length ? fromFallback : fromCapture;
    if (domValue && !isGarbledKeyBuffer(domValue)) {
      return domValue;
    }
    return "";
  }

  const fromBuffer = cleanScannedText(keyBuffer);
  if (fromFallback && (isGarbledKeyBuffer(fromBuffer) || fromFallback.length >= fromBuffer.length)) {
    return fromFallback.length >= fromCapture.length ? fromFallback : fromCapture;
  }

  let best = "";
  [fromBuffer, fromCapture, fromFallback].forEach((part) => {
    if (part.length > best.length) best = part;
  });
  return best;
}

function completeBufferedScan() {
  const raw = readPendingScan();
  keyBuffer = "";
  clearCaptureInputValue();
  clearIdleFlush();
  if (!raw || raw.length < MIN_SCAN_LENGTH || !scanCallback) {
    if (wedgeSessionActive && typeof fallbackScanGetter === "function") {
      scheduleIdleFlush();
    } else if (wedgeSessionActive && !fallbackScanGetter) {
      setTimeout(() => focusSymbolWedgeCapture(), 30);
    }
    return;
  }
  if (isGarbledKeyBuffer(raw)) {
    if (typeof fallbackScanGetter === "function") {
      scheduleIdleFlush();
      return;
    }
  }

  blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
  try {
    if (progressCallback && !fallbackScanGetter) {
      progressCallback(raw);
    }
    scanCallback(raw);
  } catch (e) {
    console.warn("symbolWedgeSession scan callback:", e);
  } finally {
    setTimeout(() => {
      if (!wedgeSessionActive || fallbackScanGetter) return;
      focusSymbolWedgeCapture();
    }, 50);
  }
}

function isNavigationKey(e) {
  const code = e.keyCode || e.which;
  const key = e?.key;
  return (
    key === "Search" ||
    key === "F3" ||
    code === 84 ||
    code === 114 ||
    code === 122
  );
}

function onWedgeKeyDown(e) {
  if (wedgeSessionActive && isNavigationKey(e)) {
    swallowEnter(e);
    return;
  }

  if (isEnterKey(e) || isTabKey(e)) {
    if (wedgeSessionActive) {
      swallowEnter(e);
      if (scanCallback) {
        completeBufferedScan();
      }
      return;
    }
    if (shouldBlockEnterNow()) {
      swallowEnter(e);
    }
    return;
  }

  if (!wedgeSessionActive || e.repeat) return;

  const ch = keyEventToChar(e);
  if (!ch || e.ctrlKey || e.metaKey || e.altKey) return;

  lastKeyAt = Date.now();
  blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;

  // Visible field mode: never swallow printable keys — Android wedge delivers the
  // real barcode through DOM input events; keydown keyCodes are often garbage (0000).
  if (typeof fallbackScanGetter === "function") {
    return;
  }

  keyBuffer += ch;
  swallowEnter(e);
  if (captureInput) {
    captureInput.value = keyBuffer;
  }
  if (progressCallback && keyBuffer.length >= 1 && !fallbackScanGetter) {
    try {
      progressCallback(keyBuffer);
    } catch (err) {
      console.warn("symbolWedgeSession progress callback:", err);
    }
  }
  scheduleIdleFlush();
}

function onWedgeKeyPress(e) {
  if ((isEnterKey(e) || isTabKey(e)) && (wedgeSessionActive || shouldBlockEnterNow())) {
    swallowEnter(e);
    if (wedgeSessionActive && scanCallback) {
      completeBufferedScan();
    }
  }
}

function onWedgeKeyUp(e) {
  if ((isEnterKey(e) || isTabKey(e)) && shouldBlockEnterNow()) {
    swallowEnter(e);
  }
}

function onShieldBeforeInput(e) {
  if (!shouldBlockEnterNow()) return;
  if (e.inputType === "insertLineBreak" || e.data === "\n" || e.data === "\r") {
    e.preventDefault();
  }
}

function onShieldSubmit(e) {
  if (!shouldBlockEnterNow()) return;
  e.preventDefault();
  e.stopPropagation();
}

function attachWedgeListeners() {
  if (wedgeTeardown || typeof window === "undefined") return;
  window.addEventListener("keydown", onWedgeKeyDown, CAPTURE_OPTS);
  window.addEventListener("keypress", onWedgeKeyPress, CAPTURE_OPTS);
  window.addEventListener("keyup", onWedgeKeyUp, CAPTURE_OPTS);
  window.addEventListener("beforeinput", onShieldBeforeInput, CAPTURE_OPTS);
  window.addEventListener("submit", onShieldSubmit, CAPTURE_OPTS);
  wedgeTeardown = () => {
    window.removeEventListener("keydown", onWedgeKeyDown, CAPTURE_OPTS);
    window.removeEventListener("keypress", onWedgeKeyPress, CAPTURE_OPTS);
    window.removeEventListener("keyup", onWedgeKeyUp, CAPTURE_OPTS);
    window.removeEventListener("beforeinput", onShieldBeforeInput, CAPTURE_OPTS);
    window.removeEventListener("submit", onShieldSubmit, CAPTURE_OPTS);
  };
}

/**
 * Capture wedge keystrokes at window level (works even when the input lags).
 * ownerId — unique per table row; re-focuses without tearing down when unchanged.
 */
export function startSymbolWedgeSession(
  onScan,
  onProgress,
  ownerId,
  getFallbackValue,
  { useHiddenCapture = false } = {}
) {
  if (
    wedgeSessionActive &&
    ownerId &&
    activeWedgeOwnerId === ownerId &&
    wedgeTeardown
  ) {
    scanCallback = onScan;
    progressCallback = onProgress || null;
    fallbackScanGetter = getFallbackValue || null;
    blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
    if (useHiddenCapture) {
      ensureCaptureInput();
      focusSymbolWedgeCapture();
    }
    return;
  }

  stopSymbolWedgeSession({ immediate: true });
  activeWedgeOwnerId = ownerId || null;
  fallbackScanGetter = getFallbackValue || null;
  wedgeSessionActive = true;
  enterShieldActive = true;
  scanCallback = onScan;
  progressCallback = onProgress || null;
  keyBuffer = "";
  lastKeyAt = 0;
  blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
  attachWedgeListeners();
  if (useHiddenCapture) {
    ensureCaptureInput();
    focusSymbolWedgeCapture();
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => focusSymbolWedgeCapture());
    }
  }
}

/** @deprecated use startSymbolWedgeSession */
export function installSymbolScanEnterShield() {
  startSymbolWedgeSession(() => {});
}

export function extendSymbolScanEnterShield(ms = POST_SCAN_ENTER_MS) {
  blockEnterUntil = Date.now() + ms;
}

function teardownWedgeListeners() {
  if (wedgeTeardown) {
    wedgeTeardown();
    wedgeTeardown = null;
  }
  blockEnterUntil = 0;
}

export function stopSymbolWedgeSessionForOwner(ownerId, { immediate = false } = {}) {
  if (ownerId && activeWedgeOwnerId && ownerId !== activeWedgeOwnerId) {
    return;
  }
  stopSymbolWedgeSession({ immediate });
}

export function stopSymbolWedgeSession({ immediate = false } = {}) {
  wedgeSessionActive = false;
  enterShieldActive = false;
  activeWedgeOwnerId = null;
  fallbackScanGetter = null;
  scanCallback = null;
  progressCallback = null;
  keyBuffer = "";
  clearIdleFlush();
  clearCaptureInputValue();

  if (immediate) {
    teardownWedgeListeners();
    if (captureInput && document.activeElement === captureInput) {
      captureInput.blur();
    }
    return;
  }

  const finish = () => {
    if (Date.now() < blockEnterUntil) {
      setTimeout(finish, blockEnterUntil - Date.now() + 50);
      return;
    }
    teardownWedgeListeners();
  };
  finish();
}

/** @deprecated use stopSymbolWedgeSession */
export function uninstallSymbolScanEnterShield() {
  stopSymbolWedgeSession();
}

/* ---- Global scanner guard: works for ANY focused text input on mobile web ----
 *
 * Monitors DOM `input` event timing.  When ≥3 events arrive within 50 ms of
 * each other (scanner burst speed) on a text input:
 *   1. Sets a 2-second Enter/Tab block so the scanner's trailing newline does
 *      not navigate Chrome or submit the form.
 *   2. Schedules an 800 ms idle callback that normalises the focused field's
 *      value (strips Sincoole prefix garbage like "\000026").
 *
 * If a dedicated wedge session is active (location field) that session handles
 * its own Enter blocking and normalisation — the guard skips those fields.
 */

let globalScanGuardCount = 0;
let globalScanGuardTeardown = null;
let scanGuardLastInputAt = 0;
let scanGuardInputCount = 0;
let scanGuardBlockEnterUntil = 0;
let scanGuardIdleTimer = null;

const SCAN_GUARD_GAP_MS = 50;       // input events < 50 ms apart → scanner burst
const SCAN_GUARD_MIN_INPUTS = 3;    // need ≥3 fast events before activating
const SCAN_GUARD_ENTER_BLOCK_MS = 2000;
const SCAN_GUARD_IDLE_MS = 800;

function applyScanGuardNormalization() {
  scanGuardIdleTimer = null;
  scanGuardInputCount = 0;
  if (wedgeSessionActive) return; // let the dedicated wedge session handle its field
  try {
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (!active) return;
    const tag = (active.tagName || "").toUpperCase();
    if (tag !== "INPUT" && tag !== "TEXTAREA") return;
    if (active.readOnly || active.disabled) return;
    const raw = active.value;
    if (!raw) return;
    const clean = normalizeWedgeScanText(raw);
    if (clean && clean !== raw) {
      // Mirror the React controlled-input trick used in syncDomScanValue:
      // reset the value tracker so React sees the change, then fire `input`.
      const tracker = active._valueTracker;
      if (tracker) tracker.setValue("");
      active.value = clean;
      active.dispatchEvent(new Event("input", { bubbles: true }));
    }
  } catch (_) {}
}

function onScanGuardInput(e) {
  if (!globalScanGuardCount) return;
  if (wedgeSessionActive) return;
  const target = e.target;
  if (!target) return;
  const tag = (target.tagName || "").toUpperCase();
  if (tag !== "INPUT" && tag !== "TEXTAREA") return;

  const now = Date.now();
  const gap = now - scanGuardLastInputAt;
  scanGuardLastInputAt = now;

  if (gap <= SCAN_GUARD_GAP_MS) {
    scanGuardInputCount++;
  } else {
    scanGuardInputCount = 1;
  }

  if (scanGuardInputCount >= SCAN_GUARD_MIN_INPUTS) {
    scanGuardBlockEnterUntil = now + SCAN_GUARD_ENTER_BLOCK_MS;
    if (scanGuardIdleTimer) clearTimeout(scanGuardIdleTimer);
    scanGuardIdleTimer = setTimeout(applyScanGuardNormalization, SCAN_GUARD_IDLE_MS);
  }
}

function onScanGuardKeydown(e) {
  if (!globalScanGuardCount) return;
  if (!isEnterKey(e) && !isTabKey(e)) return;
  if (Date.now() < scanGuardBlockEnterUntil) {
    swallowEnter(e);
    // Trigger idle normalization if not already scheduled
    if (!scanGuardIdleTimer) {
      scanGuardIdleTimer = setTimeout(applyScanGuardNormalization, SCAN_GUARD_IDLE_MS);
    }
  }
}

/**
 * Install a ref-counted global scanner guard that makes every text input on
 * the page work with a hardware barcode scanner (keyboard-wedge mode):
 *   – Enter/Tab after a scan burst is blocked (won't submit the form).
 *   – The scanned value is normalised (prefix garbage stripped) on idle.
 *
 * Call once per mounted form; balance with releaseGlobalScannerGuard().
 */
export function installGlobalScannerGuard() {
  globalScanGuardCount++;
  if (globalScanGuardTeardown || typeof window === "undefined") return;
  window.addEventListener("input", onScanGuardInput, CAPTURE_OPTS);
  window.addEventListener("keydown", onScanGuardKeydown, CAPTURE_OPTS);
  globalScanGuardTeardown = () => {
    window.removeEventListener("input", onScanGuardInput, CAPTURE_OPTS);
    window.removeEventListener("keydown", onScanGuardKeydown, CAPTURE_OPTS);
    if (scanGuardIdleTimer) { clearTimeout(scanGuardIdleTimer); scanGuardIdleTimer = null; }
    scanGuardInputCount = 0;
    scanGuardBlockEnterUntil = 0;
  };
}

export function releaseGlobalScannerGuard() {
  globalScanGuardCount = Math.max(0, globalScanGuardCount - 1);
  if (globalScanGuardCount === 0 && globalScanGuardTeardown) {
    globalScanGuardTeardown();
    globalScanGuardTeardown = null;
  }
}
